import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import { Info, ChevronUp, ChevronDown, Clock, Rocket, ListTodo, MessageSquarePlus, ExternalLink, Database, RefreshCw, CheckCircle, XCircle, AlertTriangle, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CombinedFooter = ({ isAdmin = false, sidebarCollapsed = false, hasSidebar = false }) => {
  // Version state
  const [versionInfo, setVersionInfo] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVersionLoading, setIsVersionLoading] = useState(true);

  // Query log state (admin only)
  const [queries, setQueries] = useState([]);
  const [queryLogExpanded, setQueryLogExpanded] = useState(false);
  const [queryLoading, setQueryLoading] = useState(false);
  const [queryLogVisible, setQueryLogVisible] = useState(true);

  // Calculate left position based on sidebar state
  const leftPosition = hasSidebar 
    ? (sidebarCollapsed ? 'left-16' : 'left-64')
    : 'left-0';

  // Fetch version info + config
  useEffect(() => {
    const fetchVersionInfo = async () => {
      try {
        const response = await axios.get(`${API}/version`);
        setVersionInfo(response.data);
      } catch (error) {
        console.error("Error fetching version info:", error);
        setVersionInfo({
          version: "Alpha",
          release_date: "2026.01.30",
          suggestions_url: "https://docs.google.com/spreadsheets/d/1JrqDYf2haF7wwwDFZmZK3UYOy8O56L2OB18wBELZvQ0/edit?gid=0#gid=0",
          changelog: [],
          backlog: []
        });
      } finally {
        setIsVersionLoading(false);
      }
    };
    fetchVersionInfo();
    // Check query log visibility
    axios.get(`${API}/system-config/public`).then(r => {
      if (r.data.query_log_visible === false) setQueryLogVisible(false);
    }).catch(() => {});
  }, []);

  // Fetch queries (admin only)
  const fetchQueries = useCallback(async () => {
    if (!isAdmin) return;
    setQueryLoading(true);
    try {
      const response = await axios.get(`${API}/admin/query-log`, { withCredentials: true });
      setQueries(response.data.queries || []);
    } catch (error) {
      console.error("Error fetching query log:", error);
    } finally {
      setQueryLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) {
      fetchQueries();
      const interval = setInterval(fetchQueries, 30000);
      return () => clearInterval(interval);
    }
  }, [isAdmin, fetchQueries]);

  const handleClearQueries = async () => {
    try {
      await axios.post(`${API}/admin/query-log/clear`, {}, { withCredentials: true });
      setQueries([]);
    } catch (error) {
      console.error("Error clearing query log:", error);
    }
  };

  const formatDate = (dateStr) => {
    if (dateStr && dateStr.includes(".")) {
      const parts = dateStr.split(".");
      if (parts.length >= 4) {
        const year = parts[0];
        const month = parts[1];
        const day = parts[2];
        const time = parts[3];
        const hours = time.substring(0, 2);
        const mins = time.substring(2, 4);
        const monthNames = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
        const monthName = monthNames[parseInt(month) - 1] || month;
        return `${day} ${monthName} ${year} - ${hours}:${mins}`;
      }
    }
    return dateStr;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "P0": return "bg-red-100 text-red-700";
      case "P1": return "bg-amber-100 text-amber-700";
      case "P2": return "bg-blue-100 text-blue-700";
      default: return "bg-slate-100 text-slate-700";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "success":
        return <CheckCircle className="h-3 w-3 text-green-500" />;
      case "failed":
      case "error":
        return <XCircle className="h-3 w-3 text-red-500" />;
      case "blocked_disabled":
        return <AlertTriangle className="h-3 w-3 text-amber-500" />;
      default:
        return <Clock className="h-3 w-3 text-slate-400" />;
    }
  };

  const formatTime = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch {
      return "-";
    }
  };

  if (isVersionLoading || !versionInfo) {
    return null;
  }

  const recentQueries = queries.slice(-20).reverse();
  const successCount = queries.filter(q => q.status === "success").length;
  const errorCount = queries.filter(q => q.status !== "success").length;

  return (
    <div 
      className={`fixed bottom-0 ${leftPosition} right-0 z-20 transition-all duration-200`}
    >
      {/* Query Log Section (Admin Only, if visible) */}
      {isAdmin && queryLogVisible && (
        <div className={`bg-slate-900 text-white transition-all ${queryLogExpanded ? 'h-[240px]' : 'h-10'}`}>
          {/* Query Log Header */}
          <div 
            className="h-10 px-4 flex items-center justify-between cursor-pointer hover:bg-slate-800 transition-colors"
            onClick={() => setQueryLogExpanded(!queryLogExpanded)}
          >
            <div className="flex items-center gap-3">
              <Database className="h-4 w-4 text-cyan-400" />
              <span className="text-sm font-medium">Query Log</span>
              <Badge className="bg-slate-700 text-slate-300 text-xs">
                {queries.length} queries
              </Badge>
              {successCount > 0 && (
                <Badge className="bg-green-900 text-green-300 text-xs">
                  {successCount} ok
                </Badge>
              )}
              {errorCount > 0 && (
                <Badge className="bg-red-900 text-red-300 text-xs">
                  {errorCount} err
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-6 text-slate-400 hover:text-white hover:bg-slate-700"
                onClick={(e) => { e.stopPropagation(); fetchQueries(); }}
              >
                <RefreshCw className={`h-3 w-3 ${queryLoading ? 'animate-spin' : ''}`} />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-6 text-slate-400 hover:text-red-400 hover:bg-slate-700"
                onClick={(e) => { e.stopPropagation(); handleClearQueries(); }}
                title="Limpiar log"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
              {queryLogExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </div>
          </div>

          {/* Query Log Expanded Content */}
          {queryLogExpanded && (
            <div className="h-[200px] overflow-auto p-2">
              <TooltipProvider>
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-800">
                  <tr className="text-slate-400">
                    <th className="text-left p-1 w-16">Estado</th>
                    <th className="text-left p-1 w-20">Hora</th>
                    <th className="text-left p-1 w-20">Fuente</th>
                    <th className="text-left p-1 w-20">Tipo</th>
                    <th className="text-left p-1 w-24">Módulo</th>
                    <th className="text-right p-1 w-16">Duración</th>
                    <th className="text-left p-1">Query</th>
                  </tr>
                </thead>
                <tbody>
                  {recentQueries.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center text-slate-500 p-4">
                        No hay queries registrados
                      </td>
                    </tr>
                  ) : (
                    recentQueries.map((query, idx) => (
                      <tr key={idx} className="border-t border-slate-800 hover:bg-slate-800/50">
                        <td className="p-1">
                          <div className="flex items-center gap-1">
                            {getStatusIcon(query.status)}
                            <span className={`text-[10px] ${
                              query.status === "success" ? "text-green-400" :
                              query.status === "blocked_disabled" ? "text-amber-400" :
                              "text-red-400"
                            }`}>
                              {query.status}
                            </span>
                          </div>
                        </td>
                        <td className="p-1 text-slate-400 font-mono">{formatTime(query.timestamp)}</td>
                        <td className="p-1">
                          {query.data_source && (
                            <Badge className={`text-[10px] px-1 ${
                              query.data_source === 'lakebase' 
                                ? 'bg-emerald-900 text-emerald-300' 
                                : 'bg-blue-900 text-blue-300'
                            }`}>
                              {query.data_source === 'lakebase' ? 'Lakebase' : 'Lakehouse'}
                            </Badge>
                          )}
                        </td>
                        <td className="p-1">
                          <Badge className="bg-cyan-900 text-cyan-300 text-[10px] px-1">
                            {query.query_type}
                          </Badge>
                        </td>
                        <td className="p-1 text-slate-300">{query.module}</td>
                        <td className="p-1 text-right text-slate-400 font-mono">
                          {query.duration_ms ? `${query.duration_ms}ms` : "-"}
                        </td>
                        <td className="p-1">
                          {query.query_text && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button className="text-left text-slate-500 hover:text-slate-300 truncate max-w-[200px] block">
                                  {query.query_text.substring(0, 50)}...
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="left" className="max-w-md">
                                <pre className="text-[10px] whitespace-pre-wrap font-mono">
                                  {query.query_text}
                                </pre>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              </TooltipProvider>
            </div>
          )}
        </div>
      )}

      {/* Version Footer Section */}
      <div className="bg-white/95 backdrop-blur-sm border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4">
          {/* Version Bar */}
          <div 
            className="flex items-center justify-between py-2 cursor-pointer"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
                <span className="text-sm font-medium text-slate-700">
                  Datahub {versionInfo.version}
                </span>
              </div>
              <span className="text-xs text-slate-400">|</span>
              <span className="text-xs text-slate-500">
                {formatDate(versionInfo.release_date)}
              </span>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Suggestions Link */}
              {versionInfo.suggestions_url && (
                <a
                  href={versionInfo.suggestions_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-xs text-amber-600 hover:text-amber-700 transition-colors px-2 py-1 rounded hover:bg-amber-50"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MessageSquarePlus className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Sugerencias</span>
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
              
              <Dialog>
                <DialogTrigger asChild>
                  <button 
                    className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-indigo-600 transition-colors px-2 py-1 rounded hover:bg-slate-100"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Info className="h-3.5 w-3.5" />
                    Ver detalles
                  </button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <div className="p-1.5 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600">
                        <Rocket className="h-4 w-4 text-white" />
                      </div>
                      Datahub v{versionInfo.version}
                    </DialogTitle>
                  </DialogHeader>
                  
                  <Tabs defaultValue="changelog" className="mt-4">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="changelog" className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        Changelog
                      </TabsTrigger>
                      <TabsTrigger value="backlog" className="flex items-center gap-2">
                        <ListTodo className="h-4 w-4" />
                        Backlog
                      </TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="changelog" className="mt-4 max-h-[50vh] overflow-y-auto pr-2">
                      <div className="space-y-6">
                        {versionInfo.changelog.map((release, idx) => (
                          <div key={idx} className="relative pl-4 border-l-2 border-slate-200">
                            <div className="absolute -left-[9px] top-0 h-4 w-4 rounded-full bg-white border-2 border-indigo-500" />
                            <div className="mb-2">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs font-semibold">
                                  v{release.version}
                                </Badge>
                                <span className="text-xs text-slate-500">
                                  {formatDate(release.date)}
                                </span>
                              </div>
                            </div>
                            <ul className="space-y-1.5">
                              {release.changes.map((change, changeIdx) => (
                                <li 
                                  key={changeIdx} 
                                  className="text-sm text-slate-600 flex items-start gap-2"
                                >
                                  <span className="text-indigo-500 mt-1">•</span>
                                  {change}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </TabsContent>
                    
                    <TabsContent value="backlog" className="mt-4 max-h-[50vh] overflow-y-auto pr-2">
                      <div className="space-y-4">
                        <p className="text-sm text-slate-500 mb-4">
                          Próximas funcionalidades planificadas para Datahub:
                        </p>
                        {versionInfo.backlog.map((item, idx) => (
                          <div 
                            key={idx} 
                            className="p-4 rounded-lg border border-slate-200 bg-slate-50/50 hover:bg-white transition-colors"
                          >
                            <div className="flex items-start gap-3">
                              <Badge className={`${getPriorityColor(item.priority)} text-xs font-semibold`}>
                                {item.priority}
                              </Badge>
                              <div>
                                <h4 className="font-medium text-slate-800">{item.title}</h4>
                                <p className="text-sm text-slate-500 mt-1">{item.description}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </TabsContent>
                  </Tabs>
                </DialogContent>
              </Dialog>
              
              <button 
                className="p-1 hover:bg-slate-100 rounded transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(!isExpanded);
                }}
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-slate-400" />
                ) : (
                  <ChevronUp className="h-4 w-4 text-slate-400" />
                )}
              </button>
            </div>
          </div>
          
          {/* Expanded Version Info */}
          {isExpanded && (
            <div className="pb-3 pt-1 border-t border-slate-100 grid md:grid-cols-2 gap-4">
              <div>
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                  Últimos cambios (v{versionInfo.version})
                </h4>
                <ul className="space-y-1">
                  {versionInfo.changelog[0]?.changes.slice(0, 3).map((change, idx) => (
                    <li key={idx} className="text-xs text-slate-600 flex items-start gap-1.5">
                      <span className="text-emerald-500">✓</span>
                      {change}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                  Próximamente
                </h4>
                <ul className="space-y-1">
                  {versionInfo.backlog.slice(0, 2).map((item, idx) => (
                    <li key={idx} className="text-xs text-slate-600 flex items-start gap-1.5">
                      <Badge className={`${getPriorityColor(item.priority)} text-[10px] px-1 py-0`}>
                        {item.priority}
                      </Badge>
                      {item.title}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
